import { PassState } from '../../../enums/PassState.ts';
import { applyKey } from '../../../utils/object/applyKey.ts';
import { isType } from '../../../utils/typescript/is.ts';

export const PRODUCT_NAME = 'ChartCheck'; // should match the server
export const ACCESS_WEEKLY_PERMISSION = 'CHARTCHECK_CAN_ACCESS_WEEKLY_CHECK';
export const SETTINGS_PERMISSION = 'CHARTCHECK_CAN_EDIT_WEEKLY_CHECK_SETTINGS';
export const APPROVE_PERMISSION = 'CHARTCHECK_CAN_APPROVE_WEEKLY_CHECK';
export const RESEARCH_PERMISSION = 'CHARTCHECK_CAN_ACCESS_RESEARCH_MODE';
export const BATCH_PERMISSION = 'CHARTCHECK_CAN_BATCH_APPROVE';
export const BATCH_SETTINGS_PERMISSION = 'CHARTCHECK_CAN_EDIT_BATCH_SETTINGS';
export const ASSIGN_CHECK_TEMPLATE_PERMISSION = 'CHARTCHECK_CAN_APPROVE_WEEKLY_CHECK';
export const ADAPTIVE_SETTINGS_PERMISSION = 'CHARTCHECK_CAN_EDIT_ADAPTIVE_SETTINGS';

export function isCompletedStatus (status: PatientStatus) {
  return status?.toLowerCase() === 'completed';
}

export function isEndOfTreatmentStatus(status: PatientStatus) {
  return status?.toLowerCase() === 'endOfTreatment'.toLowerCase();
}

export const PatientStatus = {
  Initial: 'initial',
  Weekly: 'weekly',
  EndOfTreatment: 'endOfTreatment',
  Completed: 'completed',
  CheckToday: 'checkedToday',
  Research: 'research'
} as const;

export type PatientStatus = ValueOf<typeof PatientStatus>;

export const statuses: Record<PatientStatus, string> = {
  [PatientStatus.Research]: 'Research',
  [PatientStatus.Initial]: 'First Weekly Check',
  [PatientStatus.Weekly]: 'Weekly Chart Check',
  [PatientStatus.EndOfTreatment]: 'EoT Chart Check',
  [PatientStatus.Completed]: 'Completed',
  [PatientStatus.CheckToday]: 'Checked Today'
};

export const statusFilters = [
  PatientStatus.Initial,
  PatientStatus.Weekly,
  PatientStatus.EndOfTreatment,
  PatientStatus.Completed,
  PatientStatus.CheckToday
];

export const states: Partial<Record<Lowercase<PassState>, string>> = {
  pass: 'Pass',
  fail: 'Fail',
  condition: 'Condition',
  override: 'Override'
} as const;

export const stateFilters: (keyof typeof states)[] = [
  PassState.Pass.toLowerCase() as Lowercase<PassState>,
  PassState.Fail.toLowerCase() as Lowercase<PassState>,
  PassState.Condition.toLowerCase() as Lowercase<PassState>,
  PassState.Override.toLowerCase() as Lowercase<PassState>
] as const;

type DateOptionValueBase = { key: DateOption, text: string };
export type DateOptionCustomValue = DateOptionValueBase & { custom: true };
export type DateOptionStartEndValue = DateOptionValueBase & { start: int, end: int };
export type DateOptionValue = DateOptionCustomValue | DateOptionStartEndValue;

export type DateOption = 'THIS_WEEK' | 'LAST_TWO_WEEKS' | 'THIS_MONTH' | 'CUSTOM';

const dateOptions: Record<DateOption, DateOptionValue> = {
  THIS_WEEK: { key: 'THIS_WEEK', text: 'Last 7 Days', start: -7, end: 0 },
  LAST_TWO_WEEKS: { key: 'LAST_TWO_WEEKS', text: 'Last 14 Days', start: -14, end: 0 },
  THIS_MONTH: { key: 'THIS_MONTH', text: 'Last 30 Days', start: -30, end: 0 },
  CUSTOM: { key: 'CUSTOM', text: 'Custom Range', custom: true }
} as const;

export const DateOptions: Record<string, DateOptionValue> = applyKey(dateOptions, 'key');

export const isCustomDateOption = (dateOption: DateOptionValue) =>
  isType<DateOptionCustomValue>(dateOption, d => d !== null && typeof d === 'object' && 'custom' in d);